import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { getQrAnalyticsSummary, getQrAnalyticsTrend } from "../../services";
import {
  QrAnalyticsSummaryResponse,
  QrAnalyticsTrendResponse,
  QrTrendGroupBy,
} from "../../types/qr-analytics.type";
import { UNFILTERED_OPTION } from "../SaleDepositReport/useBranchRouteOptions";
import QrDashboardFilter from "./Filter";
import ChannelSummary from "./ChannelSummary";
import ChannelChart, { ChannelMeasure, EmptyChart } from "./ChannelChart";
import AdoptionTrendChart from "./AdoptionTrendChart";
import BranchTable from "./BranchTable";
import ShopRankingTable from "./ShopRankingTable";
import "dayjs/locale/th";

dayjs.locale("th");

const API_DATE = "YYYY-MM-DD";

/** "all" is a UI value, not a filter — the API takes an absent parameter to mean everything. */
const toApiParam = (value: string): string | undefined =>
  value === UNFILTERED_OPTION ? undefined : value;

/**
 * แดชบอร์ด QR — how much of the bill population is being paid by เงินโอน+QR, and where.
 *
 * The owner reads the channels as two groups — เงินสด against เงินโอน+QR — with the group
 * then split into เงินโอน and QR, so every headline, delta, trend and ranking on this screen
 * is the group's; QR alone survives as a breakdown inside it.
 *
 * Counts PWA bills ONLY. A bill synced in from vansale carries no payment channel and none
 * can be recorded against it, so including those would divide a handful of real QR bills by
 * a third of a million legacy ones and report near-zero adoption everywhere — a number about
 * the rollout, not about how shops pay. The backend applies that filter; the note below says
 * so on the screen, because a branch that has not cut over now shows no data rather than 0%,
 * and the reader has to be able to tell those apart.
 *
 * Every figure comes from `cash_sales.payment_type`, the same column the sale-deposit and
 * รายงานการโอน screens read, so this screen and the money screens agree by construction.
 */
export default function QrDashboard() {
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState<Dayjs>(() =>
    dayjs().startOf("month"),
  );
  const [dateTo, setDateTo] = useState<Dayjs>(() => dayjs());
  const [branchCode, setBranchCode] = useState<string>(UNFILTERED_OPTION);
  const [routeCode, setRouteCode] = useState<string>(UNFILTERED_OPTION);
  const [groupBy, setGroupBy] = useState<QrTrendGroupBy>("day");
  const [measure, setMeasure] = useState<ChannelMeasure>("bill_count");

  const [summary, setSummary] = useState<QrAnalyticsSummaryResponse>();
  const [trend, setTrend] = useState<QrAnalyticsTrendResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const apiDateFrom = dateFrom.format(API_DATE);
  const apiDateTo = dateTo.format(API_DATE);
  const apiBranch = toApiParam(branchCode);
  const apiRoute = toApiParam(routeCode);

  const onChangeDateRange = useCallback((from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const onLoad = useCallback(async () => {
    setLoading(true);
    setError(false);
    const params = {
      date_from: apiDateFrom,
      date_to: apiDateTo,
      branch_code: apiBranch,
      route_code: apiRoute,
    };
    try {
      const [summaryResult, trendResult] = await Promise.all([
        getQrAnalyticsSummary(params),
        getQrAnalyticsTrend({ ...params, group_by: groupBy }),
      ]);
      setSummary(summaryResult.data.data ?? undefined);
      setTrend(trendResult.data.data ?? undefined);
    } catch {
      setError(true);
      setSummary(undefined);
      setTrend(undefined);
    } finally {
      setLoading(false);
    }
  }, [apiDateFrom, apiDateTo, apiBranch, apiRoute, groupBy]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  /**
   * What the ผลต่าง column is measured against. The comparison window is the same LENGTH as
   * the selected one, not the previous calendar month, so the label has to describe it in
   * those terms — "จากเดือนก่อน" would be a lie for a 10-day range.
   */
  const previousLabel = useMemo(() => {
    const days = dateTo.diff(dateFrom, "day") + 1;
    return ` เทียบ ${days} วันก่อนหน้า`;
  }, [dateFrom, dateTo]);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Typography variant="h6">แดชบอร์ด QR</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ListAltIcon />}
          onClick={() => navigate("/report/qr-transaction")}
        >
          ดูรายการ QR
        </Button>
      </Box>

      <QrDashboardFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChangeDateRange={onChangeDateRange}
        branchCode={branchCode}
        onChangeBranch={setBranchCode}
        routeCode={routeCode}
        onChangeRoute={setRouteCode}
        groupBy={groupBy}
        onChangeGroupBy={setGroupBy}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        นับเฉพาะบิลที่ออกจากแอป (PWA) ซึ่งบันทึกช่องทางการชำระเงินไว้ —
        บิลจากระบบเดิม (vansale) แก้ไขวิธีจ่ายเงินไม่ได้ จึงไม่ถูกนับในหน้านี้
        สาขาที่ยังไม่เปลี่ยนระบบจะไม่มีข้อมูลแสดง
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && summary && (
        <Grid container spacing={2}>
          <Grid size={12}>
            <ChannelSummary
              totals={summary.totals}
              digitalPercentDelta={summary.change.digital_percent_delta}
              previousLabel={previousLabel}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={1}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  ช่องทางการชำระเงิน
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={measure}
                  onChange={(_, next: ChannelMeasure | null) =>
                    next && setMeasure(next)
                  }
                >
                  <ToggleButton value="bill_count">จำนวนบิล</ToggleButton>
                  <ToggleButton value="amount">ยอดเงิน</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {trend ? (
                <ChannelChart buckets={trend.buckets} measure={measure} />
              ) : (
                <EmptyChart />
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                แนวโน้มสัดส่วน เงินโอน+QR
              </Typography>
              <AdoptionTrendChart buckets={trend?.buckets ?? []} />
            </Paper>
          </Grid>

          <Grid size={12}>
            <BranchTable
              branches={summary.branches}
              previousLabel={previousLabel}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <ShopRankingTable
              title="ร้านที่จ่ายโอน/QR มากที่สุด"
              caption={{
                digital_count:
                  "เรียงตามจำนวนบิลที่จ่ายด้วยเงินโอนหรือ QR มากไปน้อย",
                digital_percent: "เรียงตามสัดส่วนบิลเงินโอน+QR มากไปน้อย",
              }}
              sortDir="desc"
              dateFrom={apiDateFrom}
              dateTo={apiDateTo}
              branchCode={apiBranch}
              routeCode={apiRoute}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <ShopRankingTable
              title="ร้านที่จ่ายโอน/QR น้อยที่สุด"
              caption={{
                digital_count:
                  "รวมร้านที่ยังจ่ายเงินสดทุกบิล เรียงร้านที่ซื้อบ่อยขึ้นก่อน",
                digital_percent:
                  "เรียงตามสัดส่วนเงินโอน+QR น้อยไปมาก ร้านที่ซื้อบ่อยขึ้นก่อน",
              }}
              sortDir="asc"
              dateFrom={apiDateFrom}
              dateTo={apiDateTo}
              branchCode={apiBranch}
              routeCode={apiRoute}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
